import { useCallback, useEffect, useMemo, useState } from "react";
import type { Locale } from "../../shared/dto";
import { TestFilterNote } from "./TestFilterNote";
import { AdminReload, AdminReloadRow } from "./AdminReload";
import { OpenClientLink } from "./OpenClient";
import { isOfflineMutation, staffMutation } from "./model";
import { duplicateIndexes } from "./client-picker";
import {
  AdminImportsFeedback,
  IMPORT_SOURCES,
  IMPORT_STATUSES,
  fillClient,
  fillCount,
  filteredImportRows,
  formatImportTime,
  importCounts,
  importConfigSourceKey,
  importFixKey,
  importReasonKeys,
  importSourceKey,
  importStatusKey,
  visibleImportRows,
  type ActionState,
  type ImportHistoryResponse,
  type ImportRow,
  type ImportSource,
  type ImportStatus
} from "./AdminImportsShared";

// One queue over every import source. Before this, a Stripe delivery lived in
// the Stripe panel, a failed dossier lived inside a client detail and a dead
// research job was visible nowhere: the admin had to know which client to open
// to find out that nothing arrived. Each row now names the source, the state,
// the reason it stopped and the one repeat that is safe from here.

export interface RetryState {
  id: string;
  state: ActionState;
  message: string | null;
}

export function AdminImportHistory(props: {
  t: (key: string) => string;
  locale: Locale;
  csrfToken: string;
  hideTestTenants: boolean;
  onHideTestTenantsChange?: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
}) {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [listState, setListState] = useState<"loading" | "ready" | "error">("loading");
  const [source, setSource] = useState<ImportSource | "all">("all");
  const [status, setStatus] = useState<ImportStatus | "all">("all");
  const [retryState, setRetryState] = useState<RetryState | null>(null);

  // A refresh after a repeat keeps the rows on screen: swapping them for the
  // loading line would blink the confirmation the operator just earned away.
  const load = useCallback(async (quiet = false) => {
    if (!quiet) setListState("loading");
    try {
      const response = await fetch("/api/admin/imports", { credentials: "include" });
      const body = await response.json() as ImportHistoryResponse;
      if (!response.ok || !body.ok || !Array.isArray(body.imports)) throw new Error("imports_load_failed");
      setRows(body.imports);
      setListState("ready");
    } catch {
      setListState("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // The counts describe the rows this panel can REACH, so they are taken from
  // the test-filtered list, not from the server census over the whole table.
  // Before this, "Selhalo: 14" stood above eighteen rows without a single
  // failure among them and the fourteen were unreachable: every one of them
  // belonged to a test tenant the global filter removes. Same law wave 4 gave
  // the notification queue - a count that disagrees with its own list is a lie
  // the reader cannot check.
  const reachable = useMemo(() => visibleImportRows(rows, props.hideTestTenants), [rows, props.hideTestTenants]);
  const visible = useMemo(() => filteredImportRows(reachable, { source, status }), [reachable, source, status]);
  const counts = useMemo(() => importCounts(reachable), [reachable]);
  const hiddenByTestFilter = rows.length - reachable.length;
  const filtersActive = source !== "all" || status !== "all";

  async function retry(row: ImportRow) {
    setRetryState({ id: row.id, state: "working", message: null });
    const response = await staffMutation(
      `/api/admin/imports/${row.source}/${encodeURIComponent(row.rowId)}/retry`,
      { method: "POST", csrfToken: props.csrfToken, body: {} }
    );
    if (!response.ok) {
      // S2-01: a repeat that never left the browser names the connection,
      // not the import.
      setRetryState({
        id: row.id,
        state: "error",
        message: isOfflineMutation(response) ? props.t("staffConsole.mutationOffline") : null
      });
      return;
    }
    const body = await response.json() as { status?: string; clientName?: string };
    const client = body.clientName ?? row.clientName ?? row.subject;
    setRetryState({
      id: row.id,
      state: "done",
      message: fillClient(
        props.t(body.status === "already_running"
          ? "staffConsole.adminImports.retryRunning"
          : "staffConsole.adminImports.retryQueued"),
        client
      )
    });
    await load(true);
  }

  return (
    <div className="admin-imports-history">
      {/* S2-12/S2-15: the panel says its name and its purpose once, in the
          summary above. The strip that opens it carries the counts it stands
          over instead of an empty band. */}
      <AdminReloadRow busy={listState === "loading"} onClick={() => void load()} t={props.t}>
        <div className="admin-imports-summary">
          <span>{fillCount(props.t("staffConsole.adminImports.summary.total"), counts.total)}</span>
          <span className={counts.failed ? "admin-import-count failed" : "admin-import-count"}>
            {fillCount(props.t("staffConsole.adminImports.summary.failed"), counts.failed)}
          </span>
          <span className={counts.pending ? "admin-import-count pending" : "admin-import-count"}>
            {fillCount(props.t("staffConsole.adminImports.summary.pending"), counts.pending)}
          </span>
        </div>
      </AdminReloadRow>
      <div className="admin-imports-filters">
        <label className="inline-select">
          <span>{props.t("staffConsole.adminImports.filter.source")}</span>
          <select value={source} onChange={(event) => setSource(event.target.value as ImportSource | "all")}>
            <option value="all">{props.t("staffConsole.adminImports.filter.all")}</option>
            {IMPORT_SOURCES.map((value) => (
              <option key={value} value={value}>{props.t(importSourceKey(value))}</option>
            ))}
          </select>
        </label>
        <label className="inline-select">
          <span>{props.t("staffConsole.adminImports.filter.status")}</span>
          <select value={status} onChange={(event) => setStatus(event.target.value as ImportStatus | "all")}>
            <option value="all">{props.t("staffConsole.adminImports.filter.all")}</option>
            {IMPORT_STATUSES.map((value) => (
              <option key={value} value={value}>{props.t(importStatusKey(value))}</option>
            ))}
          </select>
        </label>
      </div>
      {listState === "ready" && (
        <TestFilterNote shown={visible.length} hidden={hiddenByTestFilter} t={props.t}
          onShowTest={props.onHideTestTenantsChange && (() => props.onHideTestTenantsChange?.(false))} />
      )}
      {listState === "loading" && <p>{props.t("common.loading")}</p>}
      {listState === "error" && (
        <div className="admin-imports-error" role="alert">
          <p className="error-message">{props.t("staffConsole.adminImports.loadFailed")}</p>
          <AdminReload onClick={() => void load()} t={props.t} />
        </div>
      )}
      {listState === "ready" && visible.length === 0 && (
        <div className="admin-imports-empty">
          <p className="staff-empty">
            {props.t(filtersActive ? "staffConsole.adminImports.emptyFiltered" : "staffConsole.adminImports.empty")}
          </p>
          {filtersActive && (
            <button className="secondary-button compact-button" type="button"
              onClick={() => { setSource("all"); setStatus("all"); }}>
              {props.t("staffConsole.adminImports.clearFilters")}
            </button>
          )}
        </div>
      )}
      {listState === "ready" && visible.length > 0 && (
        <AdminImportRows rows={visible} locale={props.locale} t={props.t}
          retryState={retryState} onRetry={(row) => void retry(row)}
          onOpenClient={props.onOpenClient} />
      )}
    </div>
  );
}

/** What the reader can actually TELL APART in a rendered row (audit S2-20). */
export function importRowIdentity(row: ImportRow): string {
  return [row.source, row.subject, row.subjectEmail, row.status, row.occurredAt, row.note]
    .map((part) => (part ?? "").trim().toLowerCase())
    .join("|");
}

export function AdminImportRows(props: {
  rows: ImportRow[];
  locale: Locale;
  t: (key: string) => string;
  retryState: RetryState | null;
  onRetry: (row: ImportRow) => void;
  onOpenClient?: (clientId: string) => void;
}) {
  // Two rows that look identical and lead to two DIFFERENT clients are a trap:
  // "Otevřít klienta" opens one of them and nothing on screen says which. The
  // pickers got this discriminator when S-19 was fixed; it belongs to the list
  // as well, and it is the same helper.
  const duplicates = duplicateIndexes(props.rows.map(importRowIdentity));
  return (
    <div className="admin-import-rows">
      {props.rows.map((row, position) => {
        const reasonKeys = importReasonKeys(row);
        const fixKey = importFixKey(row);
        const configSourceKey = importConfigSourceKey(row);
        const rowRetry = props.retryState?.id === row.id ? props.retryState : null;
        const facts = [
          row.subjectEmail,
          row.note,
          // F148: which channel configured the purchase rides with the facts,
          // not with the reasons - nothing about it went wrong.
          configSourceKey ? props.t(configSourceKey) : null,
          row.attempts ? fillCount(props.t("staffConsole.adminImports.attempts"), row.attempts) : null
        ].filter(Boolean).join(" · ");
        return (
          <article className={`admin-import-row ${row.status}`} key={row.id}>
            <div className="admin-import-row-head">
              <span className="admin-import-source">{props.t(importSourceKey(row.source))}</span>
              <strong>
                {row.subject || props.t("staffConsole.adminImports.unknownSubject")}
                {duplicates[position] !== null && (
                  <span className="admin-import-duplicate">
                    {props.t("staffConsole.clientPickerDuplicate")
                      .replace("{index}", String(duplicates[position]))}
                  </span>
                )}
              </strong>
              <span className={`admin-import-state ${row.status}`}>{props.t(importStatusKey(row.status))}</span>
              <time dateTime={row.occurredAt}>{formatImportTime(row.occurredAt, props.locale)}</time>
            </div>
            {facts && <p className="admin-import-facts">{facts}</p>}
            {reasonKeys.map((reasonKey) => (
              <p className="admin-import-reason" key={reasonKey}>{props.t(reasonKey)}</p>
            ))}
            {fixKey && <p className="admin-import-fix">{props.t(fixKey)}</p>}
            <div className="admin-import-actions">
              {/* 🔴 S3-07: this was the one path out of Servis that had no
                  handler at all, so an ordinary click left the single-page app:
                  the drawer closed, the filters reset, the place in the list was
                  gone and every admin request was paid for again. It was also
                  the smallest target on the surface, seventeen pixels against
                  the forty-four of the same act one panel down. */}
              {row.clientId
                ? (
                  <OpenClientLink clientId={row.clientId} onOpenClient={props.onOpenClient}
                    label={props.t("staffConsole.adminImports.openClient")} />
                )
                : <span className="admin-import-note">{props.t("staffConsole.adminImports.noClient")}</span>}
              {row.retryKind && (
                <button className="secondary-button compact-button" type="button"
                  disabled={rowRetry?.state === "working"} onClick={() => props.onRetry(row)}>
                  {props.t("staffConsole.adminImports.retry")}
                </button>
              )}
              {rowRetry && (
                <AdminImportsFeedback state={rowRetry.state} message={rowRetry.message} t={props.t}
                  working={props.t("staffConsole.adminImports.retryWorking")}
                  error={props.t("staffConsole.adminImports.retryFailed")} />
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
}
